import React, { useRef } from 'react';
import { Bar } from 'react-chartjs-2';
import { Spin, Button, message } from 'antd';
import { LoadingOutlined, FileExcelOutlined, PrinterOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import moment from 'moment';

const antIcon = <LoadingOutlined style={{ fontSize: 40 }} spin />;

const NTRLChart = ({ delivered_to_ntrlhub_list, datarequested }) => {
  const chartRef = useRef(null);


  // Excel export functionality
  const exportToExcel = () => {
    try {
      if (!delivered_to_ntrlhub_list || delivered_to_ntrlhub_list.length === 0) {
        message.warning('No data available to export');
        return;
      }

      // Prepare data for Excel
      const excelData = [];
      
      // Add header row
      const sampleTypes = new Set();
      delivered_to_ntrlhub_list.forEach(monthData => {
        if (monthData.samples) {
          monthData.samples.forEach(sample => {
            sampleTypes.add(sample.sample_name);
          });
        }
      });
      
      const sortedSampleTypes = Array.from(sampleTypes).sort();
      
              // Create Excel data
        delivered_to_ntrlhub_list.forEach(monthData => {
          const row = { Month: monthData.month };
          sortedSampleTypes.forEach(sampleType => {
            const sampleData = monthData.samples && monthData.samples.find(s => s.sample_name === sampleType);
            row[sampleType.toUpperCase()] = sampleData ? sampleData.total_in_month : 0;
          });
          row['MONTHLY TOTAL'] = monthData.monthly_total || 0;
          excelData.push(row);
        });

      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Central Hub Samples Data');
      
      // Generate filename
      const filename = `Central_Hub_Samples_Report_${moment().format('DD_MM_YYYY')}.xlsx`;
      
      // Save file
      XLSX.writeFile(wb, filename);
      
      message.success('Excel file downloaded successfully!');
    } catch (error) {
      console.error('Excel export error:', error);
      message.error('Failed to export Excel file. Please try again.');
    }
  };

  // Image download functionality
  const downloadAsImage = () => {
    try {
      if (chartRef.current) {
        let canvas = null;
        
        // Try multiple ways to access the canvas (Chart.js v2 compatibility)
        if (chartRef.current.chartInstance && chartRef.current.chartInstance.canvas) {
          canvas = chartRef.current.chartInstance.canvas;
        } else if (chartRef.current.chart && chartRef.current.chart.canvas) {
          canvas = chartRef.current.chart.canvas;
        } else if (chartRef.current.canvas) {
          canvas = chartRef.current.canvas;
        } else {
          // Try to find canvas element directly
          const chartContainer = chartRef.current;
          if (chartContainer && chartContainer.querySelector) {
            canvas = chartContainer.querySelector('canvas');
          }
        }
        
        if (canvas) {
          // Use canvas toDataURL method directly
          const url = canvas.toDataURL('image/png', 1.0);
          
          // Create download link
          const link = document.createElement('a');
          link.download = `Central_Hub_Chart_${moment().format('DD_MM_YYYY')}.png`;
          link.href = url;
          
          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          message.success('Chart image downloaded successfully!');
        } else {
          throw new Error('Chart canvas not accessible');
        }
      } else {
        throw new Error('Chart reference not available');
      }
    } catch (error) {
      console.error('Image download error:', error);
      message.error('Failed to download chart image. Please try again.');
    }
  };

  // Print functionality
  const printChart = () => {
    try {
      if (chartRef.current) {
        let canvas = null;
        
        // Try multiple ways to access the canvas (Chart.js v2 compatibility)
        if (chartRef.current.chartInstance && chartRef.current.chartInstance.canvas) {
          canvas = chartRef.current.chartInstance.canvas;
        } else if (chartRef.current.chart && chartRef.current.chart.canvas) {
          canvas = chartRef.current.chart.canvas;
        } else if (chartRef.current.canvas) {
          canvas = chartRef.current.canvas;
        } else {
          // Try to find canvas element directly
          const chartContainer = chartRef.current;
          if (chartContainer && chartContainer.querySelector) {
            canvas = chartContainer.querySelector('canvas');
          }
        }
        
        if (!canvas) {
          throw new Error('Chart canvas not accessible for printing');
        }
        
        const chartImageUrl = canvas.toDataURL('image/png', 1.0);
        
        // Prepare data for print table
        const printTableRows = delivered_to_ntrlhub_list.map((monthData, index) => {
          const monthTotal = monthData.monthly_total || 0;
          return `<tr>
            <td style="text-align: center; font-weight: bold;">${monthData.month}</td>
            <td style="text-align: center;">${monthTotal}</td>
          </tr>`;
        }).join('');

        const printContent = `
          <html>
            <head>
              <title>Central Hub Samples Report - ${moment().format('DD/MM/YYYY')}</title>
              <style>
                @page {
                  margin: 0.5in;
                  size: landscape;
                }
                
                body { 
                  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; 
                  margin: 0;
                  padding: 20px;
                  background: white;
                  color: #333;
                  line-height: 1.4;
                }
                
                .print-container {
                  max-width: 100%;
                  margin: 0 auto;
                }
                
                .print-header { 
                  text-align: center; 
                  margin-bottom: 30px; 
                  border-bottom: 3px solid #1890ff;
                  padding-bottom: 20px;
                }
                
                .print-header h1 { 
                  color: #1890ff; 
                  margin-bottom: 10px;
                  font-size: 24px;
                  font-weight: bold;
                  text-transform: uppercase;
                }
                
                .chart-container {
                  text-align: center;
                  margin: 30px 0;
                }
                
                .chart-image {
                  max-width: 100%;
                  height: auto;
                  border: 1px solid #ddd;
                  border-radius: 8px;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }
                
                .data-table-container {
                  margin: 30px 0;
                }
                
                .data-table-title {
                  font-size: 18px;
                  font-weight: bold;
                  margin-bottom: 15px;
                  color: #1890ff;
                  text-align: center;
                }
                
                table { 
                  width: 100%; 
                  border-collapse: collapse; 
                  margin: 0 auto;
                  background: white;
                  font-size: 12px;
                }
                
                th { 
                  background: linear-gradient(135deg, #1890ff 0%, #0066cc 100%);
                  color: white;
                  font-weight: bold;
                  padding: 12px 8px;
                  text-align: center;
                  font-size: 12px;
                  text-transform: uppercase;
                  border: 1px solid #0066cc;
                }
                
                td { 
                  border: 1px solid #ddd; 
                  padding: 10px 8px; 
                  font-size: 11px;
                  vertical-align: middle;
                }
                
                tr:nth-child(even) { 
                  background-color: #f8f9fa; 
                }
                
                .print-footer { 
                  margin-top: 30px; 
                  text-align: center; 
                  font-size: 11px; 
                  color: #666;
                  border-top: 2px solid #1890ff;
                  padding-top: 15px;
                }
              </style>
            </head>
            <body>
              <div class="print-container">
                <div class="print-header">
                  <h1>NTRL Samples Report</h1>
                  <p style="margin: 5px 0; font-size: 14px;">Generated on ${moment().format('MMMM DD, YYYY [at] HH:mm')}</p>
                </div>
                
                <div class="chart-container">
                  <img src="${chartImageUrl}" alt="NTRL Samples Chart" class="chart-image">
                </div>
                
                <div class="data-table-container">
                  <div class="data-table-title">Monthly Samples Data</div>
                  <table>
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Total Samples</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${printTableRows}
                    </tbody>
                  </table>
                </div>
                
                <div class="print-footer">
                  <p><strong>UMOJA Central Hub Samples Tracking System</strong></p>
                  <p>This report contains confidential delivery tracking information</p>
                  <p>Report generated automatically on ${moment().format('MMMM DD, YYYY [at] HH:mm')}</p>
                </div>
              </div>
            </body>
          </html>
        `;

        // Open print window
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        
        // Wait for images to load before printing
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
        
        message.success('Print preview opened successfully!');
      }
    } catch (error) {
      console.error('Print error:', error);
      message.error('Failed to print chart. Please try again.');
    }
  };

  // Generate colors for sample types
  const getSampleTypeColors = (sampleTypes) => {
    const colorPalette = [
      '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#98D8C8', 
      '#F7DC6F', '#BB8FCE', '#85C1E9', '#F8C471', '#82E0AA', '#F1948A', '#85C1E9',
      '#F4D03F', '#A569BD', '#5DADE2', '#58D68D', '#EC7063', '#F39C12', '#8E44AD',
      '#3498DB', '#1ABC9C', '#2ECC71', '#F39C12', '#E74C3C', '#9B59B6', '#34495E'
    ];
    
    const colors = {};
    const borderColors = {};
    
    sampleTypes.forEach((sampleType, index) => {
      const color = colorPalette[index % colorPalette.length];
      colors[sampleType] = color;
      borderColors[sampleType] = color;
    });
    
    return { colors, borderColors };
  };

  const prepareChartData = () => {
    if (!delivered_to_ntrlhub_list || delivered_to_ntrlhub_list.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    // Extract months for x-axis
    const months = delivered_to_ntrlhub_list.map(item => item.month);

    // Get all unique sample types
    const allSampleTypes = new Set();
    delivered_to_ntrlhub_list.forEach(monthData => {
      if (monthData.samples) {
        monthData.samples.forEach(sample => {
          allSampleTypes.add(sample.sample_name);
        });
      }
    });

    // Convert to array and sort for consistent ordering
    const sampleTypesArray = Array.from(allSampleTypes).sort();
    
    // Generate colors for all sample types
    const { colors: sampleColors, borderColors: sampleBorderColors } = getSampleTypeColors(sampleTypesArray);

    // Create datasets for each sample type
    const datasets = sampleTypesArray.map(sampleType => {
      const data = delivered_to_ntrlhub_list.map(monthData => {
        if (monthData.samples) {
          const sampleData = monthData.samples.find(sample => sample.sample_name === sampleType);
          return sampleData ? sampleData.total_in_month : 0;
        }
        return 0;
      });

      return {
        label: sampleType.toUpperCase(),
        data: data,
        backgroundColor: sampleColors[sampleType],
        borderColor: sampleBorderColors[sampleType],
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false,
      };
    });

    return {
      labels: months,
      datasets: datasets
    };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12
          }
        }
      },
      title: {
        display: true,
        text: 'VOLUME OF SAMPLES DELIVERED TO NTRL',
        font: {
          size: 16,
          weight: 'bold'
        },
        padding: {
          top: 10,
          bottom: 30
        }
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: function(context) {
            return `Month: ${context[0].label}`;
          },
          label: function(context) {
            return `${context.dataset.label}: ${context.parsed.y} samples`;
          },
          footer: function(tooltipItems) {
            let total = 0;
            tooltipItems.forEach(function(tooltipItem) {
              total += tooltipItem.parsed.y;
            });
            return `Total: ${total} samples`;
          }
        }
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    },
    scales: {
      xAxes: [{
        display: true,
        stacked: true,
        scaleLabel: {
          display: true,
          labelString: 'MONTH',
          fontSize: 16,
          fontStyle: 'bold',
          fontFamily: 'Arial, sans-serif',
          fontColor: '#333'
        },
        gridLines: {
          display: false
        },
        ticks: {
          fontSize: 12,
          fontStyle: '500',
          fontColor: '#666'
        }
      }],
      yAxes: [{
        display: true,
        stacked: true,
        scaleLabel: {
          display: true,
          labelString: 'TOTAL NUMBER OF SAMPLES',
          fontSize: 16,
          fontStyle: 'bold',
          fontFamily: 'Arial, sans-serif',
          fontColor: '#333'
        },
        gridLines: {
          color: 'rgba(0, 0, 0, 0.1)',
          lineWidth: 1
        },
        ticks: {
          beginAtZero: true,
          maxTicksLimit: 8,
          fontSize: 12,
          fontStyle: '500',
          fontColor: '#666',
          callback: function(value, index, values) {
            // Show cleaner intervals (multiples of 50 or 100)
            if (value === 0) return '0';
            if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'K';
            }
            // Show values at nice intervals
            if (value % 100 === 0 || value % 50 === 0) {
              return value;
            }
            return '';
          }
        }
      }]
    },
    elements: {
      bar: {
        borderWidth: 1,
      }
    },
    animation: {
      duration: 1000,
      easing: 'easeInOutQuart'
    }
  };

  if (datarequested === true) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Spin size="large" indicator={antIcon}>
          <div className="content">
            <h3>Loading NTRL Data...</h3>
          </div>
        </Spin>
      </div>
    );
  }

  const chartData = prepareChartData();

  if (chartData.labels.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <div className="content">
          <h3>No NTRL data available</h3>
          <p>Please adjust your filters and try again</p>
        </div>
      </div>
    );
  }

  return (
    <div className="animated fadeIn" style={{ padding: '20px' }}>
      
      {/* Action Buttons */}
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        
        {/* Excel Download Button */}
        <Button 
          type="primary" 
          icon={<FileExcelOutlined />}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          onClick={exportToExcel}
        >
          Download Excel
        </Button>

        {/* Image Download Button */}
        <Button 
          type="primary" 
          icon={<DownloadOutlined />}
          style={{ backgroundColor: '#1890ff', borderColor: '#1890ff' }}
          onClick={downloadAsImage}
        >
          Download Image
        </Button>

        {/* Print Button */}
        <Button 
          type="primary" 
          icon={<PrinterOutlined />}
          style={{ backgroundColor: '#fa541c', borderColor: '#fa541c' }}
          onClick={printChart}
        >
          Print Chart
        </Button>

      </div>

      {/* Chart Container */}
      <div style={{ height: '500px' }}>
        <Bar 
          ref={chartRef} 
          data={chartData} 
          options={chartOptions}
        />
      </div>
      
    </div>
  );
};

export default NTRLChart;
